/**
 * The customer's proposal, at `/p/:token`.
 *
 * The one screen in FieldOps for somebody who is not staff. No sign-in, no
 * shell, no job overlay: the token in the address is the whole credential,
 * and the server decides from it what may be seen and done (`portal.*`).
 *
 * Phone first, because that is where the email is opened. The proposal reads
 * top to bottom; the running total sticks to the foot of the screen once
 * there is anything to choose; accepting is a name, a signature drawn with a
 * finger, and a tick against the terms — the three things the signed PDF
 * then records. Every state the link can be in has its own sentence, because
 * "something went wrong" to a customer holding a price is a phone call.
 *
 * `portal.opened` is sent once, on mount, and never from the query: a refetch
 * is not a second visit. Decision 80.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { DECLINE_REASONS, defaultOptionId, proposalChoiceProblem, type DeclineReasonKey } from '@fieldops/shared'
import { Icon } from '../../components/Icon.js'
import { themeOf, themeVars } from '../../components/proposal/Letterhead.js'
import {
  ProposalPage,
  totalsFor,
  type Choice,
  type PortalView,
} from '../../components/proposal/ProposalPage.js'
import { Button, Field, Input, Spinner, Textarea } from '../../components/ui.js'
import { cn } from '../../lib/cn.js'
import { formatDate, formatDateTime, money } from '../../lib/format.js'
import { apiOrigin, errorMessage, useTRPC } from '../../lib/trpc.js'

/** The API's file paths are relative to it, not to this page. */
function absolute(path: string | null): string | null {
  if (!path) return null
  return /^https?:/.test(path) ? path : `${apiOrigin}${path}`
}

function withAbsoluteUrls(view: PortalView): PortalView {
  return {
    ...view,
    documentUrl: absolute(view.documentUrl),
    proposal: {
      ...view.proposal,
      sections: view.proposal.sections.map((section) => ({ ...section, imageUrl: absolute(section.imageUrl) })),
    },
  }
}

export function ProposalView() {
  const { token = '' } = useParams()
  const trpc = useTRPC()
  const query = useQuery({
    ...trpc.portal.view.queryOptions({ token }),
    retry: false,
    refetchOnWindowFocus: false,
  })

  const opened = useMutation(trpc.portal.opened.mutationOptions())
  const sentOpen = useRef(false)
  useEffect(() => {
    if (sentOpen.current || !query.data) return
    sentOpen.current = true
    opened.mutate({ token })
  }, [query.data, opened, token])

  useEffect(() => {
    const name = query.data?.business.name
    document.title = name ? `Proposal from ${name}` : 'Your proposal'
  }, [query.data?.business.name])

  if (query.isPending) {
    return (
      <Screen>
        <div className="grid min-h-[60vh] place-items-center">
          <Spinner className="size-6 text-ink-400" />
        </div>
      </Screen>
    )
  }

  if (query.error) {
    return (
      <Screen>
        <Notice title="This link is not working" tone="neutral">
          {errorMessage(query.error)} If you were expecting a proposal, reply to the email it came in and we
          will send you a fresh link.
        </Notice>
      </Screen>
    )
  }

  const view = withAbsoluteUrls(query.data)
  return <Proposal token={token} view={view} />
}

function Proposal({ token, view }: { token: string; view: PortalView }) {
  const { state, business, proposal, acceptance } = view
  const initialChoice: Choice = acceptance
    ? { optionId: acceptance.optionId, addOnIds: acceptance.addOnLineIds }
    : { optionId: defaultOptionId(proposal.options), addOnIds: [] }
  const [choice, setChoice] = useState<Choice>(initialChoice)
  const [mode, setMode] = useState<'reading' | 'declining'>('reading')

  const contact = [business.phone, business.email].filter(Boolean).join(' or ')

  // Only a link the customer can still act on shows what it says. A replaced
  // or changed one would show figures that are not the live ones.
  if (state === 'REPLACED' || state === 'SUPERSEDED') {
    return (
      <Screen business={business}>
        <Notice title="There is a newer version of this proposal" tone="neutral">
          {business.name} sent you an updated proposal after this one. Please use the link in the most recent
          email{contact ? `, or contact us on ${contact}` : ''}.
        </Notice>
      </Screen>
    )
  }

  if (state === 'CHANGED') {
    return (
      <Screen business={business}>
        <Notice title="This proposal is being updated" tone="neutral">
          {business.name} has made changes since this was sent to you, so it cannot be accepted from this link.
          You will receive the new version by email{contact ? ` — or contact us on ${contact}` : ''}.
        </Notice>
      </Screen>
    )
  }

  const open = state === 'OPEN'
  const totals = totalsFor(proposal, choice)
  const interactive = open && mode === 'reading'
  const hasChoices = proposal.options.length > 0 || proposal.lines.some((line) => line.isOptional)

  return (
    <Screen business={business}>
      {state === 'ACCEPTED' && acceptance ? (
        <section
          role="status"
          className="mx-4 overflow-hidden rounded-2xl bg-white font-proposal shadow-sm ring-1 ring-[var(--p-brand)] sm:mx-0"
        >
          <div className="flex items-start gap-4 bg-[var(--p-brand-soft)] px-5 py-5 sm:px-8">
            <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[var(--p-brand)] text-[var(--p-on-brand)]">
              <Icon name="check" className="size-6" />
            </span>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-ink-900">Accepted — thank you</h1>
              <p className="mt-1 text-sm leading-relaxed text-ink-700">
                Accepted by {acceptance.signedName} on {formatDateTime(acceptance.acceptedAt)} for{' '}
                <strong className="tabular-nums text-ink-900">{money(acceptance.totalIncTax)}</strong>.{' '}
                {business.name} has been told and will be in touch to book the work.
              </p>
            </div>
          </div>
          {view.documentUrl ? (
            <div className="px-5 py-3 sm:px-8">
              <a
                href={view.documentUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--p-brand-text)] underline underline-offset-2"
              >
                <Icon name="download" className="size-4" />
                Download your signed copy
              </a>
            </div>
          ) : null}
        </section>
      ) : null}

      {state === 'DECLINED' ? (
        <Notice title="You declined this proposal" tone="neutral">
          {view.declined?.at ? `On ${formatDate(view.declined.at)}. ` : ''}
          Thank you for letting us know. If you change your mind{contact ? `, contact us on ${contact}` : ', reply to the email'}.
        </Notice>
      ) : null}

      {state === 'EXPIRED' ? (
        <Notice title="This proposal has expired" tone="amber">
          {proposal.validUntil ? `It was valid until ${formatDate(proposal.validUntil)}. ` : ''}
          Prices may have changed —{' '}
          {contact ? `contact us on ${contact}` : 'reply to the email'} and we will send you a current one.
        </Notice>
      ) : null}

      <ProposalPage
        business={business}
        proposal={proposal}
        choice={choice}
        onChoice={interactive ? setChoice : undefined}
      >
        {view.documentUrl && state !== 'ACCEPTED' ? (
          <p className="mt-2 text-sm">
            <a href={view.documentUrl} target="_blank" rel="noreferrer" className="font-medium text-ink-700 underline">
              Download this proposal as a PDF
            </a>
          </p>
        ) : null}

        {open ? (
          mode === 'declining' ? (
            <DeclineForm token={token} onCancel={() => setMode('reading')} />
          ) : (
            <AcceptForm token={token} view={view} choice={choice} onDecline={() => setMode('declining')} />
          )
        ) : null}
      </ProposalPage>

      {open && mode === 'reading' && hasChoices ? (
        <div className="sticky bottom-0 z-10 border-t border-ink-200 bg-white/95 px-5 py-3 font-proposal backdrop-blur sm:bottom-4 sm:mx-auto sm:mt-4 sm:w-full sm:max-w-3xl sm:rounded-2xl sm:border sm:shadow-xl">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs text-ink-500">
                {proposal.options.find((option) => option.id === totals.chosen.optionId)?.name ?? 'Your total'}
                {choice.addOnIds.length > 0
                  ? ` + ${choice.addOnIds.length} extra${choice.addOnIds.length === 1 ? '' : 's'}`
                  : ''}
              </p>
              <p className="text-xl font-extrabold tabular-nums text-ink-900">
                {money(totals.chosen.totalIncTax)} <span className="text-xs font-normal text-ink-500">inc GST</span>
              </p>
            </div>
            <a
              href="#accept"
              className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-[var(--p-brand)] px-5 text-sm font-bold text-[var(--p-on-brand)] shadow-sm transition-transform hover:scale-[1.02]"
            >
              Accept
              <Icon name="forward" className="size-4" />
            </a>
          </div>
        </div>
      ) : null}
    </Screen>
  )
}

function Screen({ business, children }: { business?: PortalView['business']; children: React.ReactNode }) {
  return (
    <div style={themeVars(themeOf(business))} className="min-h-dvh bg-ink-100 sm:py-8">
      <main className="mx-auto flex max-w-3xl flex-col gap-4">{children}</main>
      {business ? (
        <footer className="mx-auto max-w-3xl px-5 py-8 text-center text-xs text-ink-500">
          {business.name}
          {business.taxNumber ? ` · ABN ${business.taxNumber}` : ''}
          {business.phone ? ` · ${business.phone}` : ''}
          {business.email ? ` · ${business.email}` : ''}
        </footer>
      ) : null}
    </div>
  )
}

function Notice({
  title,
  tone,
  children,
}: {
  title: string
  tone: 'green' | 'amber' | 'neutral'
  children: React.ReactNode
}) {
  return (
    <section
      role="status"
      className={cn(
        'mx-4 rounded-xl px-5 py-4 text-sm sm:mx-0',
        tone === 'green' && 'bg-brand-50 text-brand-900 ring-1 ring-brand-200',
        tone === 'amber' && 'bg-amber-50 text-amber-900 ring-1 ring-amber-200',
        tone === 'neutral' && 'bg-white text-ink-700 ring-1 ring-ink-200',
      )}
    >
      <h1 className="text-base font-semibold">{title}</h1>
      <div className="mt-1 leading-relaxed">{children}</div>
    </section>
  )
}

function AcceptForm({
  token,
  view,
  choice,
  onDecline,
}: {
  token: string
  view: PortalView
  choice: Choice
  onDecline: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [signature, setSignature] = useState<string | null>(null)
  const [agreed, setAgreed] = useState(false)
  const [tried, setTried] = useState(false)

  const accept = useMutation(
    trpc.portal.accept.mutationOptions({
      onSuccess: (next) => {
        queryClient.setQueryData(trpc.portal.view.queryKey({ token }), next)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
    }),
  )

  const totals = totalsFor(view.proposal, choice)
  const problem =
    proposalChoiceProblem({ lines: view.proposal.lines, options: view.proposal.options }, choice) ??
    (name.trim() === '' ? 'Type your name to accept.' : null) ??
    (!signature ? 'Sign in the box to accept.' : null) ??
    (!agreed ? 'Tick the box to agree to the terms.' : null)

  const submit = () => {
    setTried(true)
    if (problem || !signature) return
    accept.mutate({
      token,
      optionId: choice.optionId,
      addOnLineIds: choice.addOnIds,
      signedName: name.trim(),
      signature,
      agreed: true,
    })
  }

  return (
    <section
      id="accept"
      aria-labelledby="accept-heading"
      className="mt-10 scroll-mt-4 rounded-2xl bg-ink-50 p-5 font-proposal ring-1 ring-ink-200 sm:p-7"
    >
      <h2 id="accept-heading" className="text-xl font-bold text-ink-900">
        Accept this proposal
      </h2>
      <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 ring-1 ring-[var(--p-brand)]">
        <p className="min-w-0 text-sm text-ink-700">
          {view.proposal.options.find((option) => option.id === totals.chosen.optionId)?.name ?? 'The work'}
          {choice.addOnIds.length > 0 ? ` with ${choice.addOnIds.length} extra${choice.addOnIds.length === 1 ? '' : 's'}` : ''}
        </p>
        <strong className="shrink-0 text-lg font-extrabold tabular-nums text-[var(--p-brand-text)]">
          {money(totals.chosen.totalIncTax)} inc GST
        </strong>
      </div>

      <div className="mt-4 flex flex-col gap-4">
        <Field label="Your full name" htmlFor="accept-name">
          <Input
            id="accept-name"
            autoComplete="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="min-h-11 text-base"
          />
        </Field>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-700">Your signature</span>
          <SignaturePad onChange={setSignature} />
        </div>

        <label className="flex items-start gap-3 text-sm text-ink-700">
          <input
            type="checkbox"
            className="mt-0.5 size-5 shrink-0 accent-[var(--p-brand-text)]"
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
          />
          <span>
            I accept this proposal and the terms above, and ask {view.business.name} to carry out the work for{' '}
            {money(totals.chosen.totalIncTax)} including GST.
          </span>
        </label>

        {(tried && problem) || accept.error ? (
          <p role="alert" className="text-sm font-medium text-red-700">
            {accept.error ? errorMessage(accept.error) : problem}
          </p>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            variant="primary"
            size="lg"
            className="min-h-12 bg-[var(--p-brand)] text-base font-bold text-[var(--p-on-brand)] hover:bg-[var(--p-brand)] hover:brightness-95"
            loading={accept.isPending}
            onClick={submit}
          >
            Accept and sign
          </Button>
          <button type="button" onClick={onDecline} className="px-2 py-2 text-sm font-medium text-ink-600 underline">
            No thanks
          </button>
        </div>
      </div>
    </section>
  )
}

function DeclineForm({ token, onCancel }: { token: string; onCancel: () => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [category, setCategory] = useState<DeclineReasonKey | null>(null)
  const [note, setNote] = useState('')

  const decline = useMutation(
    trpc.portal.decline.mutationOptions({
      onSuccess: (next) => {
        queryClient.setQueryData(trpc.portal.view.queryKey({ token }), next)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
    }),
  )

  return (
    <section aria-labelledby="decline-heading" className="mt-8 border-t border-ink-200 pt-6">
      <h2 id="decline-heading" className="text-lg font-semibold text-ink-900">
        Not going ahead?
      </h2>
      <p className="mt-1 text-sm text-ink-600">It helps us to know why. This tells the business straight away.</p>

      <fieldset className="mt-4 flex flex-col gap-2">
        <legend className="sr-only">Why not</legend>
        {DECLINE_REASONS.map((reason) => (
          <label
            key={reason.key}
            className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm ring-1 ring-ink-200 has-[:checked]:ring-2 has-[:checked]:ring-ink-900"
          >
            <input
              type="radio"
              name="decline-reason"
              className="size-4 accent-ink-900"
              checked={category === reason.key}
              onChange={() => setCategory(reason.key)}
            />
            {reason.label}
          </label>
        ))}
      </fieldset>

      <div className="mt-4">
        <Field label="Anything else (optional)" htmlFor="decline-note">
          <Textarea id="decline-note" rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>

      {decline.error ? (
        <p role="alert" className="mt-3 text-sm font-medium text-red-700">
          {errorMessage(decline.error)}
        </p>
      ) : null}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <Button
          size="lg"
          className="min-h-12"
          disabled={!category}
          loading={decline.isPending}
          onClick={() => category && decline.mutate({ token, category, note })}
        >
          Decline the proposal
        </Button>
        <button type="button" onClick={onCancel} className="px-2 py-2 text-sm font-medium text-ink-600 underline">
          Back to the proposal
        </button>
      </div>
    </section>
  )
}

/**
 * A finger or a mouse, drawn onto a canvas and handed over as a PNG.
 *
 * Sized in CSS pixels and backed at the device's pixel ratio, so a signature
 * drawn on a phone is not a blur in the PDF. `touch-action: none` is what
 * stops the page scrolling under the stroke.
 */
function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const last = useRef<{ x: number; y: number } | null>(null)
  const [inked, setInked] = useState(false)

  const setup = useMemo(
    () => () => {
      const element = canvas.current
      if (!element) return
      const ratio = window.devicePixelRatio || 1
      const { width, height } = element.getBoundingClientRect()
      element.width = Math.max(1, Math.round(width * ratio))
      element.height = Math.max(1, Math.round(height * ratio))
      const context = element.getContext('2d')
      if (!context) return
      context.scale(ratio, ratio)
      context.lineWidth = 2.2
      context.lineCap = 'round'
      context.lineJoin = 'round'
      context.strokeStyle = '#111827'
    },
    [],
  )

  useEffect(() => {
    setup()
  }, [setup])

  const point = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  const finish = () => {
    if (!drawing.current) return
    drawing.current = false
    last.current = null
    if (canvas.current) onChange(canvas.current.toDataURL('image/png'))
  }

  const clear = () => {
    setup()
    setInked(false)
    onChange(null)
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <canvas
          ref={canvas}
          aria-label="Sign here"
          role="img"
          className="h-44 w-full touch-none rounded-xl bg-white ring-1 ring-ink-300 focus-visible:ring-2 focus-visible:ring-[var(--p-brand)]"
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId)
            drawing.current = true
            last.current = point(event)
            const context = canvas.current?.getContext('2d')
            if (context && last.current) {
              context.beginPath()
              context.arc(last.current.x, last.current.y, 1.1, 0, Math.PI * 2)
              context.fillStyle = '#111827'
              context.fill()
            }
            setInked(true)
          }}
          onPointerMove={(event) => {
            if (!drawing.current || !last.current) return
            const context = canvas.current?.getContext('2d')
            if (!context) return
            const next = point(event)
            context.beginPath()
            context.moveTo(last.current.x, last.current.y)
            context.lineTo(next.x, next.y)
            context.stroke()
            last.current = next
          }}
          onPointerUp={finish}
          onPointerCancel={finish}
          onPointerLeave={finish}
        />
        {!inked ? (
          <span className="pointer-events-none absolute inset-0 grid place-items-center text-sm text-ink-400">
            Sign here with your finger or mouse
          </span>
        ) : null}
        <span aria-hidden className="pointer-events-none absolute right-4 bottom-8 left-4 border-b border-ink-200" />
      </div>
      {inked ? (
        <button type="button" onClick={clear} className="flex w-fit items-center gap-1 text-xs font-medium text-ink-600 underline">
          <Icon name="refresh" className="size-3" />
          Clear and sign again
        </button>
      ) : null}
    </div>
  )
}
